import { useEnhancedBrandStore } from '../../stores/enhancedBrandStore'
import { 
  BuildingStorefrontIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { Database } from 'lucide-react'

interface KPICardProps {
  title: string
  value: string | number
  subtitle?: string
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red'
  icon?: React.ComponentType<{ className?: string }>
}

function KPICard({ title, value, subtitle, color = 'blue', icon: Icon }: KPICardProps) {
  const colorClasses = {
    blue: 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/30 border-blue-200 dark:border-blue-700',
    green: 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/30 border-green-200 dark:border-green-700',
    purple: 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/30 border-purple-200 dark:border-purple-700',
    orange: 'bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/30 border-orange-200 dark:border-orange-700',
    red: 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/30 border-red-200 dark:border-red-700'
  }

  const textColorClasses = {
    blue: 'text-blue-800 dark:text-blue-100',
    green: 'text-green-800 dark:text-green-100',
    purple: 'text-purple-800 dark:text-purple-100',
    orange: 'text-orange-800 dark:text-orange-100',
    red: 'text-red-800 dark:text-red-100'
  }


  return (
    <div className={`p-4 rounded-2xl border backdrop-blur-sm ${colorClasses[color]} ${textColorClasses[color]} transition-all duration-200 hover:shadow-lg hover:scale-[1.02] min-h-[120px]`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider opacity-70">{title}</h3>
            {Icon && (
              <div className="opacity-50" aria-hidden="true">
                <Icon className="h-5 w-5" />
              </div>
            )}
          </div>
          <div className="text-2xl font-bold mb-1 tabular-nums">
            {typeof value === 'number' && value % 1 !== 0 ? value.toFixed(1) : value}
          </div>
          {subtitle && (
            <p className="text-xs opacity-60 font-medium">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  )
}

export function KPICards() {
  const { kpis, filteredBrands, isLoading } = useEnhancedBrandStore()

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Overview Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Overview
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Key metrics and performance indicators
            </p>
          </div>
        </div>
        
        {/* KPI Cards Grid - Loading State */}
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-4 animate-pulse min-h-[120px] flex flex-col justify-between">
                <div className="flex items-center justify-between mb-2">
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
                  <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded opacity-50"></div>
                </div>
                <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded mb-1"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!kpis) return null

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Overview Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Overview
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Key metrics and performance indicators
          </p>
        </div>
      </div>
      
      {/* KPI Cards Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          title="Total Brands"
          value={kpis.totalBrands.toLocaleString()}
          subtitle={`${filteredBrands.length} filtered`}
          color="blue"
          icon={Database}
        />
        
        <KPICard
          title="Made in USA"
          value={`${kpis.confirmedUSAPercent.toFixed(1)}%`}
          subtitle="Manufacturing verified"
          color="green"
          icon={ShieldCheckIcon}
        />
        
        <KPICard
          title="Amazon Presence"
          value={`${kpis.amazonPresencePercent.toFixed(1)}%`}
          subtitle="Available on platform"
          color="orange"
          icon={BuildingStorefrontIcon}
        />
        
        <KPICard
          title="Data Quality"
          value={`${Math.round((kpis.medianConfidenceScore / 10) * 100)}%`}
          subtitle="Average confidence"
          color="purple"
          icon={ChartBarIcon}
        />
        
        <KPICard
          title="Founded Range"
          value={`${kpis.oldestFoundedYear}-${kpis.newestFoundedYear}`}
          subtitle="Year established"
          color="red"
          icon={CalendarDaysIcon}
        />
        
        <KPICard
          title="Confidence Score"
          value={`${kpis.medianConfidenceScore}/10`}
          subtitle="Median data quality"
          color="blue"
          icon={StarIcon}
        />
        </div>
      </div>
    </div>
  )
}